import React from 'react';
import styled from 'styled-components';
// Removed PageHero import as hero section is not desired
import { FiShield, FiLock, FiEye, FiMail } from 'react-icons/fi';

const PrivacyContainer = styled.div`
  margin-top: 0;
  overflow-x: hidden;
  width: 100%;
`;

const Container = styled.div`
  max-width: ${props => props.theme.breakpoints.wide};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 ${props => props.theme.spacing.md};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    /* Consistent mobile gutters */
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  
  &:first-of-type {
    padding-top: ${props => props.theme.spacing['2xl']};
  }
`;

// New simple page heading to replace hero
const PageTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.extrabold};
  color: ${props => props.theme.colors.dark};
  margin: 0 0 ${props => props.theme.spacing.md};
`;

const PageSubtitle = styled.p`
  color: ${props => props.theme.colors.gray[700]};
  font-size: ${props => props.theme.fontSizes.lg};
  margin: 0 0 ${props => props.theme.spacing.xl};
  line-height: 1.6;
`;

const ContentBox = styled.div`
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing['3xl']};
  box-shadow: ${props => props.theme.shadows.lg};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing['2xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.dark};
  margin-bottom: ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};

  svg {
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

const Text = styled.p`
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.7;
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSizes.base};

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: ${props => props.theme.colors.dark};
    font-weight: ${props => props.theme.fontWeights.semibold};
  }
`;

const List = styled.ul`
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.7;
  margin: ${props => props.theme.spacing.lg} 0;
  padding-left: ${props => props.theme.spacing.xl};

  li {
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  strong {
    color: ${props => props.theme.colors.dark};
    font-weight: ${props => props.theme.fontWeights.semibold};
  }
`;

const LastUpdated = styled.div`
  background: ${props => props.theme.colors.gray[100]};
  border-left: 4px solid ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.gray[700]};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const ContactBox = styled.div`
  background: ${props => props.theme.colors.gradient.primary};
  color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.xl};
  text-align: center;

  h3 {
    color: ${props => props.theme.colors.light};
    margin-bottom: ${props => props.theme.spacing.md};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.sm};

    svg {
      font-size: ${props => props.theme.fontSizes.lg};
    }
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: ${props => props.theme.spacing.lg};
  }

  a {
    color: ${props => props.theme.colors.light};
    font-weight: ${props => props.theme.fontWeights.semibold};
    text-decoration: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    transition: ${props => props.theme.transitions.default};

    &:hover {
      border-bottom-color: ${props => props.theme.colors.light};
    }
  }
`;

const PrivacyPolicy = () => {
  return (
    <PrivacyContainer>
      {/* Removed PageHero, using a simple heading */}
      <Section>
        <Container>
          <PageTitle>Privacy Policy</PageTitle>
          <PageSubtitle>
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal information when you use our services.
          </PageSubtitle>

          <LastUpdated>
            <strong>Last Updated:</strong> December 2024
          </LastUpdated>

          <ContentBox>
            <SectionTitle>
              <FiShield />
              Information We Collect
            </SectionTitle>
            <Text>
              At Service Hub IT Solutions, we collect information to provide better services to our users. The types of information we collect include:
            </Text>
            <List>
              <li><strong>Personal Information:</strong> Name, email address, phone number, and other contact details when you register for courses or contact us.</li>
              <li><strong>Educational Information:</strong> Course preferences, learning progress, and academic achievements.</li>
              <li><strong>Technical Information:</strong> IP address, browser type, device information, and usage data when you visit our website.</li>
              <li><strong>Payment Information:</strong> Billing details and transaction history for course purchases (processed securely through third-party payment processors).</li>
            </List>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              <FiEye />
              How We Use Your Information
            </SectionTitle>
            <Text>
              We use the collected information for the following purposes:
            </Text>
            <List>
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve our educational services and courses.</li>
              <li><strong>Communication:</strong> To send course updates, important announcements, and respond to your inquiries.</li>
              <li><strong>Personalization:</strong> To customize your learning experience and recommend relevant courses.</li>
              <li><strong>Analytics:</strong> To understand how our services are used and improve our website functionality.</li>
              <li><strong>Marketing:</strong> To send promotional materials about new courses and services (with your consent).</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations.</li>
            </List>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              <FiLock />
              Information Security
            </SectionTitle>
            <Text>
              We take the security of your personal information seriously and implement appropriate measures to protect it:
            </Text>
            <List>
              <li><strong>Data Encryption:</strong> We use SSL encryption to protect data transmitted between your device and our servers.</li>
              <li><strong>Access Controls:</strong> Only authorized personnel have access to your personal information on a need-to-know basis.</li>
              <li><strong>Regular Audits:</strong> We regularly review our security practices and update them as necessary.</li>
              <li><strong>Secure Storage:</strong> Your data is stored on secure servers with appropriate backup and recovery measures.</li>
            </List>
            <Text>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee its absolute security.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Information Sharing and Disclosure
            </SectionTitle>
            <Text>
              We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:
            </Text>
            <List>
              <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who help us operate our website and services.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights and safety.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.</li>
              <li><strong>Consent:</strong> We may share information with your explicit consent for specific purposes.</li>
            </List>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Your Rights and Choices
            </SectionTitle>
            <Text>
              You have certain rights regarding your personal information:
            </Text>
            <List>
              <li><strong>Access:</strong> You can request access to the personal information we hold about you.</li>
              <li><strong>Correction:</strong> You can request correction of inaccurate or incomplete information.</li>
              <li><strong>Deletion:</strong> You can request deletion of your personal information, subject to certain exceptions.</li>
              <li><strong>Portability:</strong> You can request a copy of your information in a structured, machine-readable format.</li>
              <li><strong>Opt-out:</strong> You can unsubscribe from marketing communications at any time.</li>
            </List>
            <Text>
              To exercise these rights, please contact us using the information provided below.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Cookies and Tracking
            </SectionTitle>
            <Text>
              Our website uses cookies and similar tracking technologies to enhance your browsing experience:
            </Text>
            <List>
              <li><strong>Essential Cookies:</strong> Required for the website to function properly.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website.</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences.</li>
              <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with your consent).</li>
            </List>
            <Text>
              You can control cookie settings through your browser preferences, but disabling certain cookies may affect website functionality.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Changes to This Privacy Policy
            </SectionTitle>
            <Text>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by:
            </Text>
            <List>
              <li>Posting the updated policy on our website</li>
              <li>Sending an email notification to registered users</li>
              <li>Displaying a prominent notice on our website</li>
            </List>
            <Text>
              We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
            </Text>
          </ContentBox>

          <ContactBox>
            <h3>
              <FiMail />
              Questions About This Policy?
            </h3>
            <p>
              If you have any questions about this Privacy Policy or how we handle your personal information, please don't hesitate to contact us.
            </p>
            <p>
              Email: <a href="mailto:privacy@servicehubitsolutions.com">privacy@servicehubitsolutions.com</a><br />
              Phone: <a href="tel:+916301313334">+91 6301313334</a>
            </p>
          </ContactBox>
        </Container>
      </Section>
    </PrivacyContainer>
  );
};

export default PrivacyPolicy;
